import {gql} from '@apollo/client';
import React from 'react';

import {assert} from 'Asserts';
import {POLICY_TEMPLATE_FRAGMENT} from 'auth/PolicyConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import PoliceablePolicyTable, {
  Policeable,
  POLICY_TABLE_PARTY_POLICEABLE_LISTING_FRAGMENT,
} from 'orgs/PoliceablePolicyTable';
import useLimitOffset from 'orgs/usePoliceablePolicyTableLimitOffset';
import * as RegistryUtil from 'registry/RegistryUtil';

import {
  BenchlingAppRegistriesListingQuery,
  BenchlingAppRegistriesListingQueryVariables,
} from './__generated/BenchlingAppRegistriesListingQuery';

export interface BenchlingAppRegistryPolicyTableProps {
  appId: string;
}

export const BENCHLING_APP_REGISTRIES_LISTING_QUERY = gql`
  query BenchlingAppRegistriesListingQuery($appId: StrictString!, $limitOffsetInput: LimitOffsetInput!)
  @skipAudit
  @engComponent(name: BENCHLING_APPS) {
    benchlingApp(id: $appId) {
      ... on BenchlingAppNode {
        id
        benchlingAppRegistriesListing(limitOffsetInput: $limitOffsetInput) {
          ... on PartyPoliceablesListingNode {
            ...PolicyTablePartyPoliceableListing
          }
        }
      }
    }
    policyTemplate(policyType: DATA) {
      ...PolicyTemplate
    }
  }
  ${POLICY_TABLE_PARTY_POLICEABLE_LISTING_FRAGMENT}
  ${POLICY_TEMPLATE_FRAGMENT}
`;

export const principalType = 'app';

export default function BenchlingAppRegistryPolicyTable(
  props: BenchlingAppRegistryPolicyTableProps
): JSX.Element | null {
  const [{limit, offset}, {setLimit, setOffset}] = useLimitOffset();

  const result = useBenchlingQuery<
    BenchlingAppRegistriesListingQuery,
    BenchlingAppRegistriesListingQueryVariables
  >(BENCHLING_APP_REGISTRIES_LISTING_QUERY, {
    variables: {
      appId: props.appId,
      limitOffsetInput: {
        limit,
        offset,
      },
    },
    fetchPolicy: 'network-only',
  });

  if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load projects; try reloading the page.');
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return (
      <PoliceablePolicyTable
        isLoading={true}
        resourceType="project"
        setOffset={setOffset}
        setLimit={setLimit}
        pagination={{offset, limit, total: 0}}
        principalType={principalType}
      />
    );
  } else {
    assert(
      !isInaccessible(result.data.benchlingApp),
      `Expected ${principalType} currently being viewed to be readable`
    );
    const registriesListing = result.data.benchlingApp.benchlingAppRegistriesListing;
    return (
      <PoliceablePolicyTable
        isLoading={false}
        resourceType="registry"
        icon={ModelTypeConstants.REGISTRY}
        setOffset={setOffset}
        setLimit={setLimit}
        pagination={{offset, limit, total: registriesListing.total}}
        policyTemplate={result.data.policyTemplate}
        getPoliceableDisplayName={(policeable: Policeable) => {
          assert(policeable.__typename === 'RegistryNode', 'Expected RegistryNode');
          return `${policeable.ownerJson.name} ${policeable.name}`;
        }}
        partyPoliceablesListing={registriesListing}
        openPoliceablePage={(policeable: Policeable) => {
          assert(policeable.__typename === 'RegistryNode', 'Expected RegistryNode');
          navigateToURL(policeable.url);
        }}
        openPoliceableSettingsPage={(policeable: Policeable) => {
          assert(policeable.__typename === 'RegistryNode', 'Expected RegistryNode');
          navigateToURL(
            RegistryUtil.getRegistryGeneralSettingsUrl(policeable.ownerJson.url_identifier, policeable.id)
          );
        }}
        principalType={principalType}
      />
    );
  }
}
